import { CircularProgress, Typography } from '@mui/material';
import { Base64 } from 'js-base64';
import { setError } from 'js/actions';
import Button from 'js/components/elements/editors/mui/Button';
import { FileWithPreview } from 'js/components/data_sources/add_ds_form/FileUploadConfiguration/FileUploadConfiguration.types';
import { FORM_TYPE_CREATE_DATA_SOURCE } from 'js/constants/formTypes';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCodeContainersById } from 'new/state/entities/selectors/transformSelectors';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import flowTypes from '../../../flowTypes';
import useFlowType from '../../hooks/useFlowType';
import {
  buildCodeContainerConfig,
  CodeSection,
  FilePreview,
  StatusMessage,
  useTestCustomProcessor,
} from 'new/components/flows/shared/customProcessor';
import classes from 'new/components/flows/shared/customProcessor/TestCustomProcessor.module.scss';
import FileUploadTestPanel from './FileUploadTestPanel';

type Props = {
  sourceConfig: any;
  buildSourceInfo: () => any;
  getProbeSampleCallback?: (credentialId: number, stage: string, payload: any) => void;
  getCustomProcessorId?: () => Promise<string>;
  useFlexFlowCustomSourceConfig?: boolean;
  uploadedFiles?: FileWithPreview[];
};

const TestCustomProcessor = ({
  sourceConfig,
  buildSourceInfo,
  getProbeSampleCallback,
  useFlexFlowCustomSourceConfig,
  getCustomProcessorId,
  uploadedFiles,
}: Props) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, Action>>();
  const transforms = useSelector(selectCodeContainersById);
  const activeType = useSelector((state: any) => state.forms.activeType);
  const credentialId = useSelector((state: any) => {
    if (activeType === FORM_TYPE_CREATE_DATA_SOURCE) {
      return state.forms[activeType]?.data.selectedCredentialId;
    }

    return state.forms[activeType]?.data.credentialId;
  });

  const flowType = useFlowType();
  const isFileUploadTestSectionEnabled = useFeatureFlag('UI_FILE_UPLOAD_TEST_SECTION_ENABLED');

  const [path, setPath] = useState('');
  const isFileUploadMode = Array.isArray(uploadedFiles);
  const { isLoadingParserOutput, isLoadingTestResult, parserOutput, testResult, error, test } =
    useTestCustomProcessor(credentialId, isFileUploadMode, getProbeSampleCallback);
  const showParserOutput = flowType !== flowTypes.custom;
  const isLoading = isLoadingParserOutput || isLoadingTestResult;

  // Surface a poll failure (network drop, transport error) the same way
  // every other failure in this component already does — `setError` is the
  // existing global error-toast dispatch used a few lines below for
  // validation failures (no code/no file/etc). Previously this was tracked
  // in hook state (useTestCustomProcessor.ts) but never read anywhere, so a
  // test run that failed to reach/parse the poll response just had its
  // spinner vanish with no visible error at all.
  useEffect(() => {
    if (error) dispatch(setError({ message: error }));
  }, [error, dispatch]);

  const onPathChanged = (value) => setPath(value);

  const onTest = async () => {
    const { packages, extra_data, customProcessorId, customProcessorCode } = sourceConfig;
    const { source_config } = buildSourceInfo();
    const savedCode = transforms[customProcessorId]?.code;
    const codeBase64 =
      sourceConfig['ui.custom_processor_mode'] === 'reusable_code_container'
        ? typeof savedCode === 'string'
          ? savedCode
          : ''
        : Base64.encode(customProcessorCode);

    if (!codeBase64) {
      dispatch(setError({ message: 'No code provided.' }));
      return;
    }

    if (!path) {
      dispatch(setError({ message: 'No file provided.' }));
      return;
    }

    const selectedFile = isFileUploadMode
      ? uploadedFiles.find((uploadedFile) => uploadedFile.name === path)
      : undefined;

    if (isFileUploadMode && !selectedFile) {
      dispatch(setError({ message: 'Selected file not found. Please re-select file.' }));
      return;
    }

    // FlexFlow runs the code as a post-processor on the saved container.
    const probeSourceConfig = useFlexFlowCustomSourceConfig
      ? {
          ...source_config,
          post_processor: [
            {
              run_engine: 'ray',
              type: 'customCodeProcessor',
              code_container_id: await getCustomProcessorId(),
              params: null,
            },
          ],
        }
      : source_config;

    test(
      codeBase64,
      path,
      {
        source_config: probeSourceConfig,
        code_container_config: buildCodeContainerConfig(packages, extra_data),
      },
      showParserOutput,
      selectedFile,
    );
  };

  const getStatusMessage = () => {
    if (!isLoadingParserOutput && isLoadingTestResult && parserOutput?.statusCode === 200) {
      return 'Parser Output Complete!';
    }
    if (!isLoadingParserOutput && !isLoadingTestResult && testResult?.statusCode === 200) {
      return 'Post Processor Output!';
    }
    return null;
  };

  if (isFileUploadMode) {
    if (!isFileUploadTestSectionEnabled) return null;

    return (
      <div className={classes.root}>
        <Typography variant="subheading2">Test Output</Typography>
        <FileUploadTestPanel
          uploadedFiles={uploadedFiles}
          path={path}
          onPathChanged={onPathChanged}
          isLoading={isLoading}
          onTest={onTest}
          statusMessage={getStatusMessage()}
          showParserOutput={showParserOutput}
          sourceConfig={sourceConfig}
          parserOutput={parserOutput}
          testResult={testResult}
          isLoadingParserOutput={isLoadingParserOutput}
          isLoadingTestResult={isLoadingTestResult}
        />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <Typography variant="subheading2">Test Output</Typography>
      <FilePreview credentialId={credentialId} path={path} onChange={onPathChanged} />
      <div className={classes.testButtonContainer}>
        <Button
          className={classes.testButton}
          variant="ghost"
          color="secondary"
          disabled={isLoading}
          onClick={onTest}
          data-testid="test-custom-processor-button"
        >
          {isLoading ? <CircularProgress size={20} /> : 'Test'}
        </Button>
        <div className={classes.infoContainer}>
          <InfoIcon />
          Processing may take up to 1 minute or more, depending on the file size and parsing rules.
          {getStatusMessage() && <StatusMessage message={getStatusMessage()} />}
        </div>
      </div>
      <div className={classes.bottom}>
        {showParserOutput && (
          <CodeSection
            heading="Nexla Parser Output"
            subheading={sourceConfig.advanced_settings}
            isLoading={isLoadingParserOutput}
            result={parserOutput}
            data-testid="nexla-parser-output"
          />
        )}
        <CodeSection
          heading="Custom Post Processor Output"
          subheading={path || '(No file selected)'}
          isLoading={isLoadingTestResult}
          result={testResult}
          data-testid="custom-post-processor-output"
        />
      </div>
    </div>
  );
};

export default TestCustomProcessor;
